import { Box, Theme, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ChangeEvent, FC, useCallback } from 'react';
import { useSnapshot } from 'valtio';
import { logger } from 'logging-utils';
import { useIsScanning } from '../../new-scan/actions/scan-actions';
import {
  removeConnectorCredentials,
  verifyAndAddCredentials,
} from '../actions/connector-configure-form-actions';
import {
  createTokenExpirationHelperMsg,
  validateSingleConnector,
} from '../actions/credentials-actions';
import {
  disableConnector,
  enableConnector,
} from '../actions/enable-disable-actions';
import ConnectorLogo from '../components/ConnectorLogo';
import EnableDisableConnectorForm from '../components/EnableDisableConnectorForm';
import OpenSourceConnectorNotice from '../components/OpenSourceConnectorNotice';
import { CredentialsType, FieldName } from '../connector-configure-types';
import {
  setAwsAccessKeyInput,
  setAwsAccessSecretInput,
  setAwsRoleArnInput,
  setClientIdInput,
  setHostUrlInput,
  setNameInput,
  setPasswordInput,
  setSecretAccessKeyInput,
  setTenantInput,
  setTokenInput,
} from '../store-actions/connector-config-form-store-action';
import ConnectorConfigureFormStore from '../stores/connector-configure-form-store';
import ConnectorConfigureStore from '../stores/connector-configure-store';
import ConnectorsStore from '../stores/connectors-store';
import { areCredentialsExpired } from '../utils/credentials-utils';
import ConnectorCredentialsFormContainer from './ConnectorCredentialsFormContainer';
import ScanStore from '../../new-scan/store/scan-store';
import { useIsUserReadOnlyRole } from '../../members/hooks/user-role-actions';

const useStyles = makeStyles()((theme: Theme) => ({
  rootForm: {
    width: '100%',
  },
  avatar: {
    marginTop: theme.spacing(2),
  },
}));

const ConnectorConfigureFormContainer: FC<
  React.PropsWithChildren<unknown>
> = () => {
  const {
    originalConnector,
    selectedConnector,
    isLoadingEnableDisable,
    invalidForm,
  } = useSnapshot(ConnectorConfigureStore);

  const {
    singleConnectorValidationInProgress,
    showSingleValidationMsg,
    singleConnectorIsValid,
  } = useSnapshot(ConnectorsStore);
  const {
    name: formNameInput,
    hostURL,
    token,
    password,
    clientId,
    secretKey,
    awsRoleArn,
    awsExternalId,
    awsAccessKey,
    awsAccessSecret,
    tenant,
  } = useSnapshot(ConnectorConfigureFormStore, { sync: true });

  const isScanInProgress = useIsScanning();
  const isReadOnlyRole = useIsUserReadOnlyRole();
  const { isDemoEnabled } = useSnapshot(ScanStore);
  const { classes } = useStyles();

  const handleEnableDisable = useCallback(
    (isEnabled: boolean) => {
      if (!selectedConnector) {
        return;
      }
      if (isEnabled) {
        enableConnector({ connectorID: selectedConnector.id });
      } else {
        disableConnector({ connectorID: selectedConnector.id });
      }
    },
    [selectedConnector],
  );

  const handleConnectorValidation = useCallback(() => {
    if (!selectedConnector) {
      return;
    }
    const { id } = selectedConnector;
    validateSingleConnector(id);
  }, [selectedConnector]);

  if (!selectedConnector) {
    return null;
  }

  const {
    credentials,
    isConfigured,
    name,
    openSourceWebsiteUrl,
    description,
    isOpenSource,
    openSourceLicense,
    credentialsType,
    openSourceAuthor,
    isEmptyOfRepos,
  } = selectedConnector;

  const isConnectorConfigured =
    (credentials.length > 0 || credentialsType === CredentialsType.None) &&
    isConfigured;

  const hasError = !!selectedConnector?.errorMessage;

  const setTextField = (event: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;

    switch (name) {
      case FieldName.hostURL:
        setHostUrlInput(value);
        break;
      case FieldName.name:
        setNameInput(value);
        break;
      case FieldName.password:
        setPasswordInput(value);
        break;
      case FieldName.token:
        setTokenInput(value);
        break;
      case FieldName.clientId:
        setClientIdInput(value);
        break;
      case FieldName.secretKey:
        setSecretAccessKeyInput(value);
        break;
      case FieldName.awsRoleArn:
        setAwsRoleArnInput(value);
        break;
      case FieldName.awsAccessKey:
        setAwsAccessKeyInput(value);
        break;
      case FieldName.awsAccessSecret:
        setAwsAccessSecretInput(value);
        break;
      case FieldName.tenant:
        setTenantInput(value);
        break;
      default:
        logger.warn('Tried to update an unknown field name');
        break;
    }
  };

  const credentialsExpired = areCredentialsExpired(selectedConnector);
  const credentialsExpirationMsg =
    createTokenExpirationHelperMsg(selectedConnector);
  const connectorHostURLProps = {
    hasError,
    name,
    value: hostURL,
    onChange: setTextField,
    isDisabled: isConnectorConfigured || isDemoEnabled || isReadOnlyRole,
  };

  return (
    <form className={classes.rootForm}>
      <Box sx={{ flexGrow: 1 }}>
        <Box display='flex' gap={2} mb={3} alignItems='center'>
          <ConnectorLogo
            connectorName={originalConnector?.name || selectedConnector.name}
            serverIconUrl={
              originalConnector?.iconURL || selectedConnector.iconURL
            }
          />
          <Typography variant='body2' ml={3}>
            {originalConnector?.description || description}
          </Typography>
        </Box>
        {(isOpenSource || credentialsType === CredentialsType.None) &&
          !selectedConnector.comingSoon && (
            <Box display='flex'>
              {isOpenSource && (
                <OpenSourceConnectorNotice
                  connectorName={name}
                  openSourceWebsite={openSourceWebsiteUrl}
                  license={openSourceLicense}
                  openSourceAuthor={openSourceAuthor}
                />
              )}
              {credentialsType === CredentialsType.None && (
                <EnableDisableConnectorForm
                  isConnectorConfigured={isConnectorConfigured}
                  connector={selectedConnector}
                  handleEnableDisableConnector={handleEnableDisable}
                  isLoading={isLoadingEnableDisable}
                />
              )}
            </Box>
          )}
        <ConnectorCredentialsFormContainer
          connector={selectedConnector}
          handleOnChange={setTextField}
          values={{
            name: formNameInput,
            token,
            password,
            hostURL,
            clientId,
            secretKey,
            awsRoleArn,
            awsExternalId,
            awsAccessKey,
            awsAccessSecret,
            tenant,
          }}
          isScanning={isScanInProgress}
          onVerifyCredentials={verifyAndAddCredentials}
          onRemoveCredentials={removeConnectorCredentials}
          isConnectorConfigured={isConnectorConfigured}
          areCredentialsExpired={credentialsExpired || !!isEmptyOfRepos}
          credentialsExpirationMsg={credentialsExpirationMsg}
          invalidForm={invalidForm}
          connectorHostURLProps={connectorHostURLProps}
          connectorValidationInProgress={singleConnectorValidationInProgress}
          validateConnector={handleConnectorValidation}
          showSingleConnectorValidationMsg={{
            shouldShow: showSingleValidationMsg,
            isValid:
              !!singleConnectorIsValid &&
              isEmptyOfRepos !== undefined &&
              !isEmptyOfRepos,
          }}
        />
      </Box>
    </form>
  );
};

export default ConnectorConfigureFormContainer;
